import { useEffect } from "react";

import LogUtil from "@/betby/utils/LogUtil";

type HostLike = Element & { shadowRoot?: ShadowRoot };

interface UseHideOrderListOnBonusPopupParams {
  enabled: boolean;
  hideOrderList: () => void;
  clearNewOrders?: () => void;
  /** 用于避免闭包过期，实时判断订单弹窗是否仍处于打开状态 */
  getIsOrderListVisible: () => boolean;
  containerId?: string;
}

/**
 * 订单弹窗打开时：监听 Betby 容器内 data-editor-id="bonusPopup" 出现，若出现则关闭订单弹窗。
 * 兼容多层 Shadow DOM 或光 DOM 的场景。
 */
export function useHideOrderListOnBonusPopup({
  enabled,
  hideOrderList,
  clearNewOrders,
  getIsOrderListVisible,
  containerId = "betby",
}: UseHideOrderListOnBonusPopupParams) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    const container = document.getElementById(containerId);
    const bonusPopupSelector = '[data-editor-id="bonusPopup"]';
    if (!container) return;

    /** 递归在节点及其所有 Shadow 子树中查找 data-editor-id="bonusPopup" 元素 */
    const findBonusPopupInNode = (node: Node): Element | null => {
      if (node.nodeType !== Node.ELEMENT_NODE) return null;
      const el = node as Element;
      if (el.getAttribute?.("data-editor-id") === "bonusPopup") return el;
      const host = el as HostLike;
      if (host.shadowRoot) {
        const inShadow = findBonusPopupInRoot(host.shadowRoot);
        if (inShadow) return inShadow;
      }
      for (const child of node.childNodes) {
        const found = findBonusPopupInNode(child);
        if (found) return found;
      }
      return null;
    };

    const findBonusPopupInRoot = (
      root: DocumentFragment | ShadowRoot,
    ): Element | null => {
      const direct = root.querySelector(bonusPopupSelector);
      if (direct) return direct;
      for (const node of root.childNodes) {
        const found = findBonusPopupInNode(node);
        if (found) return found;
      }
      return null;
    };

    const findBonusPopup = (): Element | null => {
      const host = container as HostLike;
      if (host.shadowRoot) {
        const inHost = findBonusPopupInRoot(host.shadowRoot);
        if (inHost) return inHost;
      }
      const inLight = container.querySelector(bonusPopupSelector);
      if (inLight) return inLight;
      const childHost = container.firstElementChild as HostLike | null;
      if (childHost?.shadowRoot) {
        const inChild = findBonusPopupInRoot(childHost.shadowRoot);
        if (inChild) return inChild;
      }
      for (const child of container.childNodes) {
        const found = findBonusPopupInNode(child);
        if (found) return found;
      }
      return null;
    };

    const checkAndHideOrderList = () => {
      if (!getIsOrderListVisible()) return;
      if (!findBonusPopup()) return;
      hideOrderList();
      clearNewOrders?.();
      LogUtil.info("[BetbyRenderer] 检测到 bonusPopup 出现，已关闭订单弹窗");
    };

    checkAndHideOrderList();

    const THROTTLE_MS = 120;
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const throttledCheck = () => {
      if (throttleTimer !== null) return;
      checkAndHideOrderList();
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
      }, THROTTLE_MS);
    };

    const observer = new MutationObserver(throttledCheck);

    const observeTarget = (target: Node) => {
      observer.observe(target, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "data-editor-id"],
      });
    };

    observeTarget(container);
    const host = container as HostLike;
    const childHost = container.firstElementChild as HostLike | null;
    const shadowRoot = host.shadowRoot ?? childHost?.shadowRoot;
    if (shadowRoot) {
      observeTarget(shadowRoot);
    } else {
      // Shadow Root 可能晚于容器挂载，轮询后补观察
      const shadowPollId = window.setInterval(() => {
        if (!getIsOrderListVisible()) return;
        const root =
          host.shadowRoot ??
          (container.firstElementChild as HostLike)?.shadowRoot;
        if (root) {
          observeTarget(root);
          window.clearInterval(shadowPollId);
        }
      }, 200);
      // 轮询兜底：嵌套 Shadow 或异步插入时 MutationObserver 可能观察不到，间隔略长以减轻开销
      const POLL_INTERVAL_MS = 400;
      const checkPollId = window.setInterval(
        checkAndHideOrderList,
        POLL_INTERVAL_MS,
      );
      return () => {
        observer.disconnect();
        if (throttleTimer !== null) clearTimeout(throttleTimer);
        window.clearInterval(shadowPollId);
        window.clearInterval(checkPollId);
      };
    }

    const POLL_INTERVAL_MS = 400;
    const checkPollId = window.setInterval(
      checkAndHideOrderList,
      POLL_INTERVAL_MS,
    );
    return () => {
      observer.disconnect();
      if (throttleTimer !== null) clearTimeout(throttleTimer);
      window.clearInterval(checkPollId);
    };
  }, [
    enabled,
    hideOrderList,
    clearNewOrders,
    getIsOrderListVisible,
    containerId,
  ]);
}
